import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { broadcastApi } from '../../../api';
import { useUIStore } from '../../../store';

const BroadcastHistoryPage = ({
  data = [],
  sess = { role: 'user', uid: '', mobile: '' },
}) => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [history, setHistory] = useState(data);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ type: '', message: '' });
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };
  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
  };
  const tableContainerSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  };

  const maxDate = new Date().toISOString().split('T')[0];

  useEffect(() => {
    setStartDate(maxDate);
    setEndDate(maxDate);
  }, [maxDate]);

  useEffect(() => {
    if (data && data.length > 0) {
      setHistory(data);
      setLoading(false);
    } else {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.type !== 'success' || !status.message) return;
    showSnackbar({ message: status.message, severity: 'success' });
    setStatus({ type: '', message: '' });
  }, [showSnackbar, status]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'delivered':
        return <Chip label="Delivered" color="success" size="small" variant="outlined" />;
      case 'read':
        return <Chip label="Read" color="info" size="small" variant="outlined" />;
      case 'failed':
        return <Chip label="Failed" color="error" size="small" variant="outlined" />;
      case 'queued':
        return <Chip label="Queued" color="warning" size="small" variant="outlined" />;
      case 'sent':
        return <Chip label="Sent" color="success" size="small" variant="outlined" />;
      default:
        return <Chip label={status || 'Unknown'} size="small" variant="outlined" />;
    }
  };

  const filterRecords = (records, start, end) => {
    if (!start || !end) return records;
    const startDateValue = new Date(`${start}T00:00:00`);
    const endDateValue = new Date(`${end}T23:59:59`);

    return records.filter((record) => {
      const rawDate = record.sent_at || record.created_at || record.updated_at;
      if (!rawDate) return false;
      const recordDate = new Date(rawDate);
      return recordDate >= startDateValue && recordDate <= endDateValue;
    });
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await broadcastApi.getAllRecords();
      const list = response?.data?.data || response?.data || [];
      setAllRecords(list);
      setHistory(filterRecords(list, maxDate, maxDate));
      setStatus({ type: '', message: '' });
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to load broadcast history';
      setStatus({ type: 'error', message });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    setHistory(filterRecords(allRecords, startDate, endDate));
  };

  const tableHeaders = ['#', 'Campaign Name', 'Customer Name', 'Template Name', 'Status', 'Sent At', 'Actions'];

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>Broadcast History</Typography>
              <Typography variant="body2" color="text.secondary">
                Review delivery performance by date
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              href="/broadcast"
              sx={secondaryButtonSx}
            >
              Back
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ ...cardSx, mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDate }}
              size="small"
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDate }}
              size="small"
              sx={{ width: { xs: '100%', md: 220 } }}
            />
            <Button variant="contained" onClick={handleApplyFilter} sx={{ width: { xs: '100%', md: 'auto' }, ...primaryButtonSx }}>
              Filter
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {status.message && status.type === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.message}
        </Alert>
      )}

      <Card sx={cardSx}>
        <CardContent>
          {loading ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, rowIndex) => (
                    <TableRow key={`history-skeleton-${rowIndex}`}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={60} height={28} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer sx={tableContainerSx}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((item, i) => {
                    const campaignId = item.broadcast_campaign_id || item.campaignId || item.campaign_id;
                    const campaignName = item.campaign_name || item.campaignName || campaignId || 'N/A';
                    const customerName = item.customer_name || item.customerName || item.customer_number || item.customer_id || 'N/A';
                    const templateName = item.template_name || item.templateName || 'N/A';
                    const statusValue = item.status || item.bd_msgstatus || item.messageStatus;
                    const updatedAt = item.sent_at || item.created_at || item.updated_at;

                    return (
                      <TableRow
                        key={item.id || item.bd_id || i}
                        hover
                        sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{campaignName}</TableCell>
                        <TableCell>{customerName}</TableCell>
                        <TableCell>{templateName}</TableCell>
                        <TableCell>{getStatusChip(statusValue)}</TableCell>
                        <TableCell>{formatDate(updatedAt)}</TableCell>
                        <TableCell>
                          {campaignId ? (
                            <Button
                              variant="outlined"
                              size="small"
                              href={`/broadcast/detail/${campaignId}`}
                            >
                              View
                            </Button>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {history.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Typography align="center" color="text.secondary">
                          No records available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BroadcastHistoryPage;
